import React, { useState, useContext, useEffect } from 'react';
import useSWR from 'swr';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Zoom,
  IconButton,
  CircularProgress,
  Collapse,
  Fade
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Snackbar } from 'Components';
import { AppContext } from 'Context';
import { AxiosCancelToken, dataFetcher, endpoints } from 'Api';
import { debounce } from 'lodash';
import { validate as isValidEmail } from 'email-validator';
import { Input } from '../Input';
import styles from './EmailEditModal.module.css';

const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };
const transitionTimeout = 300;

const EmailEditModal = ({ isVisible, onHide: onClose, subtitle, title, resetCodeLabel, newEmailLabel }) => {
  const { userDetails, setUserDetails, langText, autoSignIn } = useContext(AppContext);
  const accountText = langText?.settings?.account;
  const commonText = langText?.common;
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [checkingEmailAvailability, setEmailAvailabilityCheckingStatus] = useState(false);
  const [emailIsAvailable, setEmailAvailability] = useState(false);
  const [resetCodeSent, setResetCodeStatus] = useState(false);
  const [cancelToken, setCancelToken] = useState(undefined);

  const [snackbar, setSnackbar] = useState(defaultSnackbar);

  const { isValidating: sendingResetCode, mutate: sendResetCode } = useSWR([endpoints.changeEmailRequest], {
    onSuccess: ({ success, message }) => {
      if (success) {
        setResetCodeStatus(true);
        // eslint-disable-next-line
        setSnackbar({ isVisible: true, type: 'error', message: message });
      } else {
        setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while sending Reset Code' });
      }
    },
    onError: () => {
      setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while sending Reset Code' });
    },
  });

  const { mutate: checkEmailAvailability } = useSWR([endpoints.signupEmail, email, cancelToken?.token], {
    fetcher: (url, inputEmail, uniqueCancelToken) => dataFetcher(url, { email: inputEmail }, uniqueCancelToken),
    onSuccess: ({ success }) => {
      setEmailAvailabilityCheckingStatus(false);

      if (success) {
        setEmailAvailability(true);
      } else {
        setEmailAvailability(false);
        setSnackbar({ isVisible: true, type: 'error', message: 'This email is already taken' });
      }
    },
    onError: () => {
      setEmailAvailabilityCheckingStatus(false);
    },
  });

  const onHide = () => {
    setEmail('');
    setResetCode('');
    setEmailAvailability(false);
    setResetCodeStatus(false);
    setEmailAvailabilityCheckingStatus(false);
    setSnackbar(defaultSnackbar);
    onClose();
  };

  const { isValidating: updatingEmail, mutate: updateEmail } = useSWR([endpoints.updateEmailAddress, email, resetCode], {
    fetcher: (url, inputEmail, inputResetCode) => dataFetcher(url, { reset_code: inputResetCode, email: inputEmail }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setSnackbar({ isVisible: true, type: 'success', message: 'Email was updated successfully' });
        setUserDetails({ ...userDetails, email });
    autoSignIn().then(() => {
      onClose(); // close modal after autoSignIn finishes
    });
    setTimeout(() => {
      setEmail('');
      setResetCode('');
      setEmailAvailability(false);
      setResetCodeStatus(false);
      setEmailAvailabilityCheckingStatus(false);
      setSnackbar(defaultSnackbar);
    }, 300);
      } else {
        setSnackbar({ isVisible: true, type: 'error', message });
      }
    },
    onError: (err) => {
      console.log('err', err);
      setSnackbar({ isVisible: true, type: 'error', message: err?.response?.data?.message });
    },
  });

  const emailInputHandler = debounce(({ target: { value: inputEmail } }) => {
    if (isValidEmail(inputEmail)) {
      setEmail(inputEmail);
    } else {
      setEmail('');
      setEmailAvailability(false);
    }
  }, 750, { trailing: true });

  useEffect(() => {
    if (email) {
      setEmailAvailabilityCheckingStatus(false);
      checkEmailAvailability();
    }
  }, [email, setEmailAvailabilityCheckingStatus, checkEmailAvailability]);

  useEffect(() => { if (isVisible) { sendResetCode(); } }, [isVisible, sendResetCode]);

  return (
    <>
      <Dialog
        open={isVisible}
        onClose={onHide}
        TransitionComponent={Zoom}
        maxWidth="xs"
        fullWidth
        classes={{ paper: styles.dialogPaper }}
      >
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>{title}</h2>
            {subtitle && <p className={styles.modalSubtitle}>{subtitle}</p>}
          </div>
          <IconButton onClick={onHide} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className={styles.modalContent}>
          <Collapse in={sendingResetCode} timeout={transitionTimeout}>
            <Fade in={sendingResetCode} timeout={transitionTimeout}>
              <div className={styles.loadingMessage}>
                <CircularProgress size={20} className={styles.loadingIcon} />
                {accountText?.sendingResetCode || 'Sending Reset Code'}
              </div>
            </Fade>
          </Collapse>

          <Collapse in={resetCodeSent} timeout={transitionTimeout}>
            <Fade in={resetCodeSent} timeout={transitionTimeout}>
              <div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>{resetCodeLabel}</label>
                  <Input
                    noLable
                    auto-complete="one-time-code"
                    className={styles.inputField}
                    containerClassName={styles.inputFieldContainer}
                    maxLength={5}
                    onInput={(e) => {
                      const { target: { value: inputResetCode } } = e;
                      setResetCode(inputResetCode);
                    }}
                    type="code"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>{newEmailLabel}</label>
                  <Input
                     noLable
                    className={styles.inputField}
                    containerClassName={styles.inputFieldContainer}
                    isLoading={checkingEmailAvailability}
                    placeholder="example@projector.com"
                    onChange={(e) => {
                      e.persist();
                      emailInputHandler(e);
                      setEmailAvailabilityCheckingStatus(false);

                      if (cancelToken) { cancelToken.cancel(); }

                      if (!e?.target?.value) {
                        setEmail('');
                        setEmailAvailability(false);
                      } else {
                        setCancelToken(AxiosCancelToken.source());
                      }
                    }}
                    type="email"
                  />
                  {checkingEmailAvailability && (
                    <div className={styles.validationMessage}>
                      <CircularProgress size={14} className={styles.validationLoader} />
                      Checking availability...
                    </div>
                  )}
                  {emailIsAvailable && email && !checkingEmailAvailability && (
                    <div className={styles.validationMessageSuccess}>
                      ✓ Email is available
                    </div>
                  )}
                </div>
              </div>
            </Fade>
          </Collapse>
        </DialogContent>

        <DialogActions className={styles.modalActions}>
          <button
            className={styles.cancelButton}
            onClick={onHide}
            disabled={updatingEmail}
          >
            {commonText?.cancel || 'Cancel'}
          </button>
          <button
            className={styles.updateButton}
            onClick={updateEmail}
            disabled={
              checkingEmailAvailability
              || resetCode?.length !== 5
              || updatingEmail
              || sendingResetCode
              || !emailIsAvailable
            }
          >
            {updatingEmail ? (
              <>
                <CircularProgress size={16} className={styles.buttonLoader} />
                {commonText?.updating || 'Updating'}
                ...
              </>
            ) : (
              commonText?.update || 'Update'
            )}
          </button>
        </DialogActions>
      </Dialog>

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => { setSnackbar(defaultSnackbar); }}
        type={snackbar?.type}
      />
    </>
  );
};
export default EmailEditModal;
